const API_BASE = import.meta.env.VITE_API_URL || '';

// Get the user's timezone offset in minutes (e.g., -480 for PST)
function getTimezoneOffset(): string {
  return String(new Date().getTimezoneOffset());
}

class APIClient {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  getToken(): string | null {
    return this.token;
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  private async fetch<T>(path: string, options: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Timezone-Offset': getTimezoneOffset(),
      ...(options.headers as Record<string, string>),
    };

    if (this.getToken()) {
      headers['Authorization'] = `Bearer ${this.getToken()}`;
    }

    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      this.clearToken();
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'API Error');
    }

    return data;
  }

  // Auth
  async signup(email: string, password: string, name: string, familyName?: string) {
    const data = await this.fetch<any>('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, name, familyName }),
    });
    this.setToken(data.token);
    return data;
  }

  async login(email: string, password: string) {
    const data = await this.fetch<any>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(data.token);
    return data;
  }

  async logout() {
    await this.fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
    this.clearToken();
  }

  async getMe() {
    return this.fetch<any>('/api/auth/me');
  }

  async verifyEmail(token: string) {
    return this.fetch<any>('/api/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async resendVerification(email: string, language: string = 'en') {
    return this.fetch<any>('/api/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email, language }),
    });
  }

  async forgotPassword(email: string, language: string = 'en') {
    return this.fetch<any>('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email, language }),
    });
  }

  async resetPassword(token: string, password: string) {
    return this.fetch<any>('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  // Family
  async getFamily() {
    return this.fetch<any>('/api/family');
  }

  async getFamilyMembers() {
    return this.fetch<any>('/api/family/members');
  }

  async addFamilyMember(name: string, ageGroup: string, isParent: boolean = false) {
    return this.fetch<any>('/api/family/members', {
      method: 'POST',
      body: JSON.stringify({ name, ageGroup, isParent }),
    });
  }

  async switchMember(memberId: string) {
    return this.fetch<any>('/api/family/switch-member', {
      method: 'POST',
      body: JSON.stringify({ memberId }),
    });
  }

  async generateMagicLink(memberId: string) {
    return this.fetch<any>(`/api/family/members/${memberId}/magic-link`, {
      method: 'POST',
    });
  }

  async getMagicLink(memberId: string) {
    return this.fetch<any>(`/api/family/members/${memberId}/magic-link`);
  }

  async magicLogin(token: string) {
    const data = await this.fetch<any>('/api/auth/magic-login', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
    this.setToken(data.token);
    return data;
  }

  async updateLanguage(language: string) {
    return this.fetch<any>('/api/family/language', {
      method: 'PUT',
      body: JSON.stringify({ language }),
    });
  }

  // Phrases
  async getTodaysPhrase() {
    return this.fetch<any>('/api/phrases/today');
  }

  async getPhraseLibrary(focusArea?: string) {
    const params = focusArea ? `?focusArea=${focusArea}` : '';
    return this.fetch<any>(`/api/phrases/library${params}`);
  }

  async generatePhrase(focusArea?: string, customPrompt?: string, saveToLibrary: boolean = true, language?: string) {
    return this.fetch<any>('/api/phrases/generate', {
      method: 'POST',
      body: JSON.stringify({ focusArea, customPrompt, saveToLibrary, language }),
    });
  }

  async assignPhrase(memberId: string, phraseId?: string, customPhraseId?: string) {
    return this.fetch<any>('/api/phrases/assign', {
      method: 'POST',
      body: JSON.stringify({ memberId, phraseId, customPhraseId }),
    });
  }

  async completePhrase(assignmentId: string) {
    return this.fetch<any>('/api/phrases/complete', {
      method: 'POST',
      body: JSON.stringify({ assignmentId }),
    });
  }

  async getCategories() {
    return this.fetch<any>('/api/phrases/categories/list');
  }

  // Recordings
  async uploadRecording(audioBlob: Blob, phraseId: string, assignmentId: string, customPhraseId?: string) {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    formData.append('phraseId', phraseId);
    formData.append('assignmentId', assignmentId);
    if (customPhraseId) {
      formData.append('customPhraseId', customPhraseId);
    }

    const response = await fetch(`${API_BASE}/api/recordings/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.getToken()}`,
        'X-Timezone-Offset': getTimezoneOffset(),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Upload failed');
    }

    return response.json();
  }

  async getMyRecordings(limit: number = 10) {
    return this.fetch<any>(`/api/recordings/my-recordings?limit=${limit}`);
  }

  async getFamilyRecordings(limit: number = 20) {
    return this.fetch<any>(`/api/recordings/family?limit=${limit}`);
  }

  getRecordingUrl(recordingId: string): string {
    return `${API_BASE}/api/recordings/${recordingId}/audio`;
  }

  async deleteRecording(recordingId: string) {
    return this.fetch<any>(`/api/recordings/${recordingId}`, {
      method: 'DELETE',
    });
  }

  // Streaks
  async getMyStreak() {
    return this.fetch<any>('/api/streaks/me');
  }

  async getLeaderboard() {
    return this.fetch<any>('/api/streaks/leaderboard');
  }

  async getFamilyStats() {
    return this.fetch<any>('/api/streaks/family-stats');
  }

  async getMilestones() {
    return this.fetch<any>('/api/streaks/milestones');
  }

  async getHistory(limit: number = 30) {
    return this.fetch<any>(`/api/streaks/history?limit=${limit}`);
  }

  async getCategoryStats() {
    return this.fetch<any>('/api/streaks/category-stats');
  }
}

export const api = new APIClient();
